import type { MetroLine, MetroStation } from '../domain/types';

// Static snapshot: 2026-07-19.
// Official map: https://mosmetro.ru/metro-map
// Official schema endpoint used at build time only:
// https://prodapp.mosmetro.ru/api/schema/v1.0
// The application never requests this endpoint. MCD and perspective stations
// are excluded; same-named nodes are merged only when the official schema marks
// them as an interchange.

export const METRO_LINES = [
  { id: 'sokolnicheskaya', name: 'Сокольническая линия', color: '#D41317' },
  { id: 'zamoskvoretskaya', name: 'Замоскворецкая линия', color: '#4AAF50' },
  { id: 'arbatsko-pokrovskaya', name: 'Арбатско-Покровская линия', color: '#0072BA' },
  { id: 'filyovskaya', name: 'Филёвская линия', color: '#1EBCEF' },
  { id: 'koltsevaya', name: 'Кольцевая линия', color: '#915133' },
  { id: 'kaluzhsko-rizhskaya', name: 'Калужско-Рижская линия', color: '#EF7D00' },
  {
    id: 'tagansko-krasnopresnenskaya',
    name: 'Таганско-Краснопресненская линия',
    color: '#943E90',
  },
  { id: 'kalininskaya', name: 'Калининская линия', color: '#FFDD00' },
  { id: 'solntsevskaya', name: 'Солнцевская линия', color: '#FFDD00' },
  {
    id: 'serpukhovsko-timiryazevskaya',
    name: 'Серпуховско-Тимирязевская линия',
    color: '#ADABAC',
  },
  {
    id: 'lyublinsko-dmitrovskaya',
    name: 'Люблинско-Дмитровская линия',
    color: '#B4CC04',
  },
  { id: 'bolshaya-koltsevaya', name: 'Большая кольцевая линия', color: '#87CCCF' },
  { id: 'butovskaya', name: 'Бутовская линия', color: '#BAC8E8' },
  { id: 'mcc', name: 'МЦК', color: '#D41317' },
  { id: 'nekrasovskaya', name: 'Некрасовская линия', color: '#EE7BAE' },
  { id: 'troitskaya', name: 'Троицкая линия', color: '#007762' },
] as const satisfies readonly MetroLine[];

export type MetroLineId = (typeof METRO_LINES)[number]['id'];

const metroLineMap = new Map<string, MetroLine>(
  METRO_LINES.map((line) => [line.id, line]),
);

export const METRO_LINE_BY_ID: ReadonlyMap<string, MetroLine> = metroLineMap;

type StationRow = readonly [
  id: string,
  name: string,
  lineIds: readonly MetroLineId[],
];

const STATION_ROWS = [
  ["mosmetro-240", "Авиамоторная", ["kalininskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-88", "Автозаводская", ["zamoskvoretskaya", "mcc"]],
  ["mosmetro-179", "Академическая", ["kaluzhsko-rizhskaya", "troitskaya"]],
  ["mosmetro-153", "Александровский сад", ["filyovskaya"]],
  ["mosmetro-157", "Алексеевская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-148", "Алма-Атинская", ["zamoskvoretskaya"]],
  ["mosmetro-188", "Алтуфьево", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-425", "Аминьевская", ["bolshaya-koltsevaya"]],
  ["mosmetro-355", "Андроновка", ["mcc"]],
  ["mosmetro-234", "Аннино", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-117", "Арбатская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-152", "Арбатская", ["filyovskaya"]],
  ["mosmetro-42", "Аэропорт", ["zamoskvoretskaya"]],
  ["mosmetro-581", "Аэропорт Внуково", ["solntsevskaya"]],
  ["mosmetro-150", "Бабушкинская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-133", "Багратионовская", ["filyovskaya"]],
  ["mosmetro-386", "Балтийская", ["mcc"]],
  ["mosmetro-203", "Баррикадная", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-120", "Бауманская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-201", "Беговая", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-342", "Белокаменная", ["mcc"]],
  ["mosmetro-6", "Беломорская", ["zamoskvoretskaya"]],
  ["mosmetro-44", "Белорусская", ["zamoskvoretskaya", "koltsevaya"]],
  ["mosmetro-183", "Беляево", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-189", "Бибирево", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-17", "Библиотека имени Ленина", ["sokolnicheskaya"]],
  ["mosmetro-277", "Битцевский парк", ["butovskaya"]],
  ["mosmetro-273", "Борисово", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-222", "Боровицкая", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-305", "Боровское шоссе", ["solntsevskaya"]],
  ["mosmetro-155", "Ботанический сад", ["kaluzhsko-rizhskaya", "mcc"]],
  ["mosmetro-270", "Братиславская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-281", "Бульвар Адмирала Ушакова", ["butovskaya"]],
  ["mosmetro-235", "Бульвар Дмитрия Донского", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-1", "Бульвар Рокоссовского", ["sokolnicheskaya", "mcc"]],
  ["mosmetro-283", "Бунинская Аллея", ["butovskaya"]],
  ["mosmetro-249", "Бутырская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-663", "Вавиловская", ["troitskaya"]],
  ["mosmetro-269", "Варшавская", ["bolshaya-koltsevaya"]],
  ["mosmetro-156", "ВДНХ", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-370", "Верхние Котлы", ["mcc"]],
  ["mosmetro-245", "Верхние Лихоборы", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-192", "Владыкино", ["serpukhovsko-timiryazevskaya", "mcc"]],
  ["mosmetro-25", "Водный стадион", ["zamoskvoretskaya"]],
  ["mosmetro-40", "Войковская", ["zamoskvoretskaya"]],
  ["mosmetro-212", "Волгоградский проспект", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-267", "Волжская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-93", "Волоколамская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-22", "Воробьёвы горы", ["sokolnicheskaya"]],
  ["mosmetro-429", "Воронцовская", ["bolshaya-koltsevaya"]],
  ["mosmetro-216", "Выхино", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-636", "Генерала Тюленева", ["troitskaya"]],
  ["mosmetro-300", "Говорово", ["solntsevskaya"]],
  ["mosmetro-424", "Давыдково", ["bolshaya-koltsevaya"]],
  ["mosmetro-140", "Деловой центр", ["filyovskaya", "solntsevskaya"]],
  ["mosmetro-43", "Динамо", ["zamoskvoretskaya"]],
  ["mosmetro-205", "Дмитровская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-168", "Добрынинская", ["koltsevaya"]],
  ["mosmetro-142", "Домодедовская", ["zamoskvoretskaya"]],
  ["mosmetro-251", "Достоевская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-261", "Дубровка", ["lyublinsko-dmitrovskaya", "mcc"]],
  ["mosmetro-219", "Жулебино", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-368", "ЗИЛ", ["mcc", "troitskaya"]],
  ["mosmetro-383", "Зорге", ["mcc"]],
  ["mosmetro-430", "Зюзино", ["bolshaya-koltsevaya"]],
  ["mosmetro-275", "Зябликово", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-349", "Измайлово", ["mcc"]],
  ["mosmetro-124", "Измайловская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-182", "Калужская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-98", "Кантемировская", ["zamoskvoretskaya"]],
  ["mosmetro-266", "Каховская", ["bolshaya-koltsevaya"]],
  ["mosmetro-95", "Каширская", ["zamoskvoretskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-112", "Киевская", ["arbatsko-pokrovskaya", "filyovskaya", "koltsevaya"]],
  ["mosmetro-166", "Китай-город", ["kaluzhsko-rizhskaya", "tagansko-krasnopresnenskaya"]],
  ["mosmetro-497", "Кленовый бульвар", ["bolshaya-koltsevaya"]],
  ["mosmetro-263", "Кожуховская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-92", "Коломенская", ["zamoskvoretskaya"]],
  ["mosmetro-640", "Коммунарка", ["troitskaya"]],
  ["mosmetro-12", "Комсомольская", ["sokolnicheskaya", "koltsevaya"]],
  ["mosmetro-184", "Коньково", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-387", "Коптево", ["mcc"]],
  ["mosmetro-639", "Корниловская", ["troitskaya"]],
  ["mosmetro-398", "Косино", ["nekrasovskaya"]],
  ["mosmetro-220", "Котельники", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-147", "Красногвардейская", ["zamoskvoretskaya"]],
  ["mosmetro-174", "Краснопресненская", ["koltsevaya"]],
  ["mosmetro-11", "Красносельская", ["sokolnicheskaya"]],
  ["mosmetro-13", "Красные Ворота", ["sokolnicheskaya"]],
  ["mosmetro-259", "Крестьянская застава", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-18", "Кропоткинская", ["sokolnicheskaya"]],
  ["mosmetro-97", "Крылатское", ["arbatsko-pokrovskaya"]],
  ["mosmetro-372", "Крымская", ["mcc", "troitskaya"]],
  ["mosmetro-206", "Кузнецкий мост", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-214", "Кузьминки", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-100", "Кунцевская", ["arbatsko-pokrovskaya", "filyovskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-119", "Курская", ["arbatsko-pokrovskaya", "koltsevaya"]],
  ["mosmetro-137", "Кутузовская", ["filyovskaya", "mcc"]],
  ["mosmetro-178", "Ленинский проспект", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-217", "Лермонтовский проспект", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-278", "Лесопарковая", ["butovskaya"]],
  ["mosmetro-392", "Лефортово", ["bolshaya-koltsevaya"]],
  ["mosmetro-578", "Лианозово", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-388", "Лихоборы", ["mcc"]],
  ["mosmetro-347", "Локомотив", ["mcc"]],
  ["mosmetro-293", "Ломоносовский проспект", ["solntsevskaya"]],
  ["mosmetro-15", "Лубянка", ["sokolnicheskaya"]],
  ["mosmetro-377", "Лужники", ["mcc"]],
  ["mosmetro-400", "Лухмановская", ["nekrasovskaya"]],
  ["mosmetro-268", "Люблино", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-242", "Марксистская", ["kalininskaya"]],
  ["mosmetro-250", "Марьина Роща", ["lyublinsko-dmitrovskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-272", "Марьино", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-45", "Маяковская", ["zamoskvoretskaya"]],
  ["mosmetro-149", "Медведково", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-211", "Менделеевская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-291", "Минская", ["solntsevskaya"]],
  ["mosmetro-91", "Митино", ["arbatsko-pokrovskaya"]],
  ["mosmetro-296", "Мичуринский проспект", ["solntsevskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-410", "Мнёвники", ["bolshaya-koltsevaya"]],
  ["mosmetro-99", "Молодёжная", ["arbatsko-pokrovskaya"]],
  ["mosmetro-139", "Москва-Сити", ["filyovskaya", "mcc"]],
  ["mosmetro-94", "Мякинино", ["arbatsko-pokrovskaya"]],
  ["mosmetro-226", "Нагатинская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-496", "Нагатинский Затон", ["bolshaya-koltsevaya"]],
  ["mosmetro-227", "Нагорная", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-409", "Народное Ополчение", ["bolshaya-koltsevaya"]],
  ["mosmetro-228", "Нахимовский проспект", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-401", "Некрасовка", ["nekrasovskaya"]],
  ["mosmetro-357", "Нижегородская", ["bolshaya-koltsevaya", "mcc", "nekrasovskaya"]],
  ["mosmetro-428", "Новаторская", ["bolshaya-koltsevaya", "troitskaya"]],
  ["mosmetro-237", "Новогиреево", ["kalininskaya"]],
  ["mosmetro-236", "Новокосино", ["kalininskaya"]],
  ["mosmetro-48", "Новокузнецкая", ["zamoskvoretskaya"]],
  ["mosmetro-39", "Новомосковская", ["sokolnicheskaya", "troitskaya"]],
  ["mosmetro-306", "Новопеределкино", ["solntsevskaya"]],
  ["mosmetro-177", "Новослободская", ["koltsevaya"]],
  ["mosmetro-359", "Новохохловская", ["mcc"]],
  ["mosmetro-187", "Новоясеневская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-181", "Новые Черёмушки", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-298", "Озёрная", ["solntsevskaya"]],
  ["mosmetro-246", "Окружная", ["lyublinsko-dmitrovskaya", "mcc"]],
  ["mosmetro-396", "Окская", ["nekrasovskaya"]],
  ["mosmetro-170", "Октябрьская", ["koltsevaya", "kaluzhsko-rizhskaya"]],
  ["mosmetro-199", "Октябрьское поле", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-38", "Ольховая", ["sokolnicheskaya"]],
  ["mosmetro-116", "Орехово", ["zamoskvoretskaya"]],
  ["mosmetro-190", "Отрадное", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-16", "Охотный Ряд", ["sokolnicheskaya"]],
  ["mosmetro-49", "Павелецкая", ["zamoskvoretskaya", "koltsevaya"]],
  ["mosmetro-384", "Панфиловская", ["mcc"]],
  ["mosmetro-19", "Парк культуры", ["sokolnicheskaya", "koltsevaya"]],
  ["mosmetro-111", "Парк Победы", ["arbatsko-pokrovskaya", "solntsevskaya"]],
  ["mosmetro-123", "Партизанская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-125", "Первомайская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-238", "Перово", ["kalininskaya"]],
  ["mosmetro-403", "Петровский парк", ["bolshaya-koltsevaya"]],
  ["mosmetro-195", "Петровско-Разумовская", ["serpukhovsko-timiryazevskaya", "lyublinsko-dmitrovskaya"]],
  ["mosmetro-265", "Печатники", ["lyublinsko-dmitrovskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-128", "Пионерская", ["filyovskaya"]],
  ["mosmetro-191", "Планерная", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-375", "Площадь Гагарина", ["mcc"]],
  ["mosmetro-241", "Площадь Ильича", ["kalininskaya"]],
  ["mosmetro-118", "Площадь Революции", ["arbatsko-pokrovskaya"]],
  ["mosmetro-200", "Полежаевская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-223", "Полянка", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-638", "Потапово", ["sokolnicheskaya"]],
  ["mosmetro-232", "Пражская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-9", "Преображенская площадь", ["sokolnicheskaya"]],
  ["mosmetro-37", "Прокшино", ["sokolnicheskaya"]],
  ["mosmetro-210", "Пролетарская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-24", "Проспект Вернадского", ["sokolnicheskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-159", "Проспект Мира", ["koltsevaya", "kaluzhsko-rizhskaya"]],
  ["mosmetro-180", "Профсоюзная", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-204", "Пушкинская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-580", "Пыхтино", ["solntsevskaya"]],
  ["mosmetro-90", "Пятницкое шоссе", ["arbatsko-pokrovskaya"]],
  ["mosmetro-294", "Раменки", ["solntsevskaya"]],
  ["mosmetro-308", "Рассказовка", ["solntsevskaya"]],
  ["mosmetro-7", "Речной вокзал", ["zamoskvoretskaya"]],
  ["mosmetro-158", "Рижская", ["kaluzhsko-rizhskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-257", "Римская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-340", "Ростокино", ["mcc"]],
  ["mosmetro-34", "Румянцево", ["sokolnicheskaya"]],
  ["mosmetro-215", "Рязанский проспект", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-209", "Савёловская", ["serpukhovsko-timiryazevskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-35", "Саларьево", ["sokolnicheskaya"]],
  ["mosmetro-154", "Свиблово", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-229", "Севастопольская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-244", "Селигерская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-122", "Семёновская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-224", "Серпуховская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-102", "Славянский бульвар", ["arbatsko-pokrovskaya"]],
  ["mosmetro-115", "Смоленская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-151", "Смоленская", ["filyovskaya"]],
  ["mosmetro-41", "Сокол", ["zamoskvoretskaya"]],
  ["mosmetro-351", "Соколиная гора", ["mcc"]],
  ["mosmetro-10", "Сокольники", ["sokolnicheskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-303", "Солнцево", ["solntsevskaya"]],
  ["mosmetro-196", "Спартак", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-21", "Спортивная", ["sokolnicheskaya"]],
  ["mosmetro-253", "Сретенский бульвар", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-395", "Стахановская", ["nekrasovskaya"]],
  ["mosmetro-385", "Стрешнево", ["mcc"]],
  ["mosmetro-96", "Строгино", ["arbatsko-pokrovskaya"]],
  ["mosmetro-138", "Студенческая", ["filyovskaya"]],
  ["mosmetro-160", "Сухаревская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-193", "Сходненская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-165", "Таганская", ["koltsevaya", "tagansko-krasnopresnenskaya"]],
  ["mosmetro-46", "Тверская", ["zamoskvoretskaya"]],
  ["mosmetro-47", "Театральная", ["zamoskvoretskaya"]],
  ["mosmetro-213", "Текстильщики", ["tagansko-krasnopresnenskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-422", "Терехово", ["bolshaya-koltsevaya"]],
  ["mosmetro-89", "Технопарк", ["zamoskvoretskaya"]],
  ["mosmetro-198", "Тимирязевская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-169", "Третьяковская", ["kaluzhsko-rizhskaya", "kalininskaya"]],
  ["mosmetro-33", "Тропарёво", ["sokolnicheskaya"]],
  ["mosmetro-252", "Трубная", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-225", "Тульская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-162", "Тургеневская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-194", "Тушинская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-637", "Тютчевская", ["troitskaya"]],
  ["mosmetro-185", "Тёплый Стан", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-361", "Угрешская", ["mcc"]],
  ["mosmetro-202", "Улица 1905 года", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-233", "Улица Академика Янгеля", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-282", "Улица Горчакова", ["butovskaya"]],
  ["mosmetro-399", "Улица Дмитриевского", ["nekrasovskaya"]],
  ["mosmetro-280", "Улица Скобелевская", ["butovskaya"]],
  ["mosmetro-279", "Улица Старокачаловская", ["butovskaya"]],
  ["mosmetro-23", "Университет", ["sokolnicheskaya"]],
  ["mosmetro-635", "Университет Дружбы Народов", ["troitskaya"]],
  ["mosmetro-579", "Физтех", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-36", "Филатов Луг", ["sokolnicheskaya"]],
  ["mosmetro-136", "Фили", ["filyovskaya"]],
  ["mosmetro-129", "Филёвский парк", ["filyovskaya"]],
  ["mosmetro-248", "Фонвизинская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-20", "Фрунзенская", ["sokolnicheskaya"]],
  ["mosmetro-2", "Ховрино", ["zamoskvoretskaya"]],
  ["mosmetro-382", "Хорошёво", ["mcc"]],
  ["mosmetro-405", "Хорошёвская", ["bolshaya-koltsevaya"]],
  ["mosmetro-101", "Царицыно", ["zamoskvoretskaya"]],
  ["mosmetro-218", "Цветной бульвар", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-404", "ЦСКА", ["bolshaya-koltsevaya"]],
  ["mosmetro-8", "Черкизовская", ["sokolnicheskaya"]],
  ["mosmetro-230", "Чертановская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-221", "Чеховская", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-14", "Чистые пруды", ["sokolnicheskaya"]],
  ["mosmetro-255", "Чкаловская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-175", "Шаболовская", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-381", "Шелепиха", ["mcc"]],
  ["mosmetro-274", "Шипиловская", ["lyublinsko-dmitrovskaya"]],
  ["mosmetro-239", "Шоссе Энтузиастов", ["kalininskaya", "mcc"]],
  ["mosmetro-197", "Щукинская", ["tagansko-krasnopresnenskaya"]],
  ["mosmetro-126", "Щёлковская", ["arbatsko-pokrovskaya"]],
  ["mosmetro-121", "Электрозаводская", ["arbatsko-pokrovskaya", "bolshaya-koltsevaya"]],
  ["mosmetro-397", "Юго-Восточная", ["nekrasovskaya"]],
  ["mosmetro-26", "Юго-Западная", ["sokolnicheskaya"]],
  ["mosmetro-231", "Южная", ["serpukhovsko-timiryazevskaya"]],
  ["mosmetro-186", "Ясенево", ["kaluzhsko-rizhskaya"]],
  ["mosmetro-577", "Яхромская", ["lyublinsko-dmitrovskaya"]],
] as const satisfies readonly StationRow[];

function requireMetroLine(id: MetroLineId): MetroLine {
  const line = metroLineMap.get(id);
  if (!line) {
    throw new Error('Неизвестная линия метро в справочнике: ' + id);
  }
  return line;
}

export const METRO_STATIONS: readonly MetroStation[] = Object.freeze(
  STATION_ROWS.map(([id, name, lineIds]) =>
    Object.freeze({
      id,
      name,
      lines: Object.freeze(lineIds.map(requireMetroLine)),
    }),
  ),
);

const metroStationMap = new Map<string, MetroStation>(
  METRO_STATIONS.map((station) => [station.id, station]),
);

export const METRO_STATION_BY_ID: ReadonlyMap<string, MetroStation> =
  metroStationMap;

const russianCollator = new Intl.Collator('ru-RU', {
  sensitivity: 'base',
  usage: 'sort',
});

function normalizeMetroQuery(value: string): string {
  return value
    .normalize('NFKC')
    .trim()
    .toLocaleLowerCase('ru-RU')
    .replaceAll('ё', 'е')
    .replace(/\s+/g, ' ');
}

const metroSearchIndex = METRO_STATIONS.map((station) => ({
  station,
  normalizedName: normalizeMetroQuery(station.name),
}));

export function getMetroLineById(id: string): MetroLine | undefined {
  return metroLineMap.get(id);
}

export function getMetroStationById(id: string): MetroStation | undefined {
  return metroStationMap.get(id);
}

export function searchMetroStations(
  query: string,
  limit = 8,
): readonly MetroStation[] {
  const normalizedQuery = normalizeMetroQuery(query);
  const safeLimit = Number.isFinite(limit)
    ? Math.min(Math.max(Math.trunc(limit), 0), METRO_STATIONS.length)
    : 0;

  if (!normalizedQuery || safeLimit === 0) {
    return [];
  }

  return metroSearchIndex
    .filter(({ normalizedName }) => normalizedName.includes(normalizedQuery))
    .sort((left, right) => {
      const prefixDifference =
        Number(!left.normalizedName.startsWith(normalizedQuery)) -
        Number(!right.normalizedName.startsWith(normalizedQuery));

      return (
        prefixDifference ||
        russianCollator.compare(left.station.name, right.station.name) ||
        left.station.id.localeCompare(right.station.id)
      );
    })
    .slice(0, safeLimit)
    .map(({ station }) => station);
}
